import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { ClinicalSynthesis } from "@/components/ClinicalSynthesis";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  FilePlus2,
  Mail,
  Pencil,
  Trash2,
  CheckCircle2,
  FileText,
  Clock,
  CalendarClock,
  Phone,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

const statusMeta = {
  brouillon: { label: "Brouillon", cls: "bg-[#EAE6DF] text-[#757169]" },
  genere: { label: "E-mail prêt", cls: "bg-[#D4A373]/20 text-[#B7854f]" },
  envoye: { label: "Envoyé", cls: "bg-[#7B968B]/20 text-[#5e7a6e]" },
};

const PatientDetail = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [sessionDate, setSessionDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [creating, setCreating] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", language: "fr", notes: "" });

  const load = async () => {
    try {
      const [p, s, a] = await Promise.all([
        api.get(`/patients/${patientId}`),
        api.get(`/patients/${patientId}/sessions`),
        api.get(`/patients/${patientId}/appointments`),
      ]);
      setPatient(p.data);
      setEditForm({ name: p.data.name, email: p.data.email || "", phone: p.data.phone || "", language: p.data.language || "fr", notes: p.data.notes || "" });
      setSessions(s.data);
      setAppointments(a.data);
    } catch (e) {
      toast.error("Patient introuvable");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const createSession = async () => {
    if (!transcript.trim()) {
      toast.error("Collez d'abord la transcription de la séance");
      return;
    }
    setCreating(true);
    try {
      const res = await api.post(`/patients/${patientId}/sessions`, {
        transcript,
        session_date: new Date(`${sessionDate}T12:00:00Z`).toISOString(),
      });
      toast.success("Séance créée");
      setNewOpen(false);
      setTranscript("");
      setSessionDate(format(new Date(), "yyyy-MM-dd"));
      navigate(`/sessions/${res.data.id}`);
    } catch (e) {
      toast.error("Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const saveEdit = async () => {
    try {
      const res = await api.put(`/patients/${patientId}`, editForm);
      setPatient(res.data);
      setEditOpen(false);
      toast.success("Fiche mise à jour");
    } catch (e) {
      toast.error("Erreur de mise à jour");
    }
  };

  const deletePatient = async () => {
    if (!window.confirm("Supprimer ce patient et toutes ses séances ?")) return;
    try {
      await api.delete(`/patients/${patientId}`);
      toast.success("Patient supprimé");
      navigate("/dashboard");
    } catch (e) {
      toast.error("Erreur de suppression");
    }
  };

  const toggleActive = async (value) => {
    try {
      const res = await api.put(`/patients/${patientId}`, { active: value });
      setPatient(res.data);
      toast.success(value ? "Patient réactivé" : "Patient désactivé");
    } catch (e) {
      toast.error("Erreur de mise à jour");
    }
  };

  const deleteSession = async (e, sessionId) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer définitivement cette séance ?")) return;
    try {
      await api.delete(`/sessions/${sessionId}`);
      setSessions((list) => list.filter((s) => s.id !== sessionId));
      toast.success("Séance supprimée");
    } catch (err) {
      toast.error("Erreur de suppression");
    }
  };

  if (loading || !patient) {
    return (
      <AppLayout>
        <div className="h-40 bg-white/60 rounded-2xl border border-[#EAE6DF] animate-pulse" />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <button
        onClick={() => navigate("/dashboard")}
        data-testid="back-btn"
        className="flex items-center gap-1.5 text-sm text-[#757169] hover:text-[#2D2C28] transition-colors mb-5"
      >
        <ArrowLeft className="w-4 h-4" /> Retour aux patients
      </button>

      {/* Patient header card */}
      <div className="bg-white rounded-2xl border border-[#EAE6DF] p-6 shadow-[0_4px_20px_rgba(45,44,40,0.04)]">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#7B968B]/15 text-[#7B968B] flex items-center justify-center font-heading font-semibold text-2xl">
              {patient.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="font-heading text-2xl font-semibold text-[#2D2C28] flex items-center gap-2">
                {patient.name}
                {patient.active === false && (
                  <Badge data-testid="inactive-badge" className="rounded-full bg-[#EAE6DF] text-[#757169] border-0 font-medium">
                    Inactif
                  </Badge>
                )}
              </h1>
              <p className="text-[#757169] text-sm mt-0.5">
                {patient.email || "Aucune adresse e-mail"}
              </p>
              {patient.phone && (
                <p className="text-[#757169] text-sm mt-0.5 flex items-center gap-1.5" data-testid="patient-phone">
                  <Phone className="w-3.5 h-3.5" /> {patient.phone}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 mr-1 cursor-pointer" title="Patient actif / inactif">
              <Switch
                data-testid="patient-active-toggle"
                checked={patient.active !== false}
                onCheckedChange={toggleActive}
              />
              <span className="text-sm text-[#757169] hidden sm:inline">
                {patient.active !== false ? "Actif" : "Inactif"}
              </span>
            </label>
            <Button
              variant="outline"
              onClick={() => setEditOpen(true)}
              data-testid="edit-patient-btn"
              className="rounded-xl border-[#EAE6DF] text-[#575349]"
            >
              <Pencil className="w-4 h-4 mr-2" /> Modifier
            </Button>
            <Button
              variant="outline"
              onClick={deletePatient}
              data-testid="delete-patient-btn"
              className="rounded-xl border-[#EAE6DF] text-[#B56B6B] hover:bg-[#B56B6B]/5"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {patient.notes && (
          <div className="mt-5 pt-5 border-t border-[#EAE6DF]">
            <p className="text-xs font-medium text-[#9c988f] uppercase tracking-wide mb-1">Notes</p>
            <p className="text-[#575349] leading-relaxed whitespace-pre-wrap">{patient.notes}</p>
          </div>
        )}
      </div>

      {/* Upcoming appointments (Calendly) */}
      {appointments.length > 0 && (
        <div className="mt-8" data-testid="patient-appointments-section">
          <h2 className="font-heading text-xl font-medium text-[#2D2C28] mb-3 flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-[#7B968B]" strokeWidth={1.5} /> À venir
          </h2>
          <div className="space-y-2">
            {appointments.map((a) => (
              <div
                key={a.calendly_event_uri}
                data-testid={`upcoming-appointment-${a.calendly_event_uri.split("/").pop()}`}
                className="bg-[#7B968B]/8 border border-[#7B968B]/20 rounded-2xl p-4 flex items-center gap-3"
              >
                <CalendarClock className="w-5 h-5 text-[#7B968B] shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="font-medium text-[#2D2C28] text-sm">
                    {format(parseISO(a.start_time), "EEEE d MMMM yyyy 'à' HH:mm", { locale: fr })}
                  </p>
                  <p className="text-xs text-[#757169]">{a.event_name} · via Calendly</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sessions */}
      <div className="flex items-center justify-between mt-10 mb-4">
        <h2 className="font-heading text-xl font-medium text-[#2D2C28]">Historique des séances</h2>
        <NewSessionDialog
          open={newOpen}
          setOpen={setNewOpen}
          transcript={transcript}
          setTranscript={setTranscript}
          sessionDate={sessionDate}
          setSessionDate={setSessionDate}
          creating={creating}
          onCreate={createSession}
        />
      </div>

      {sessions.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-[#EAE6DF] p-10 text-center">
          <FileText className="w-10 h-10 text-[#C0BBB0] mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-[#757169]">
            Aucune séance enregistrée. Importez la transcription de votre première séance.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map((s) => {
            const meta = statusMeta[s.status] || statusMeta.brouillon;
            return (
              <button
                key={s.id}
                onClick={() => navigate(`/sessions/${s.id}`)}
                data-testid={`session-card-${s.id}`}
                className="group w-full text-left bg-white rounded-2xl border border-[#EAE6DF] p-5 shadow-[0_4px_20px_rgba(45,44,40,0.04)] hover:border-[#C07761]/40 transition-all"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-11 h-11 rounded-xl bg-[#C07761]/10 text-[#C07761] flex items-center justify-center shrink-0">
                      {s.status === "envoye" ? (
                        <CheckCircle2 className="w-5 h-5" strokeWidth={1.5} />
                      ) : (
                        <Clock className="w-5 h-5" strokeWidth={1.5} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-heading font-medium text-[#2D2C28]">
                        Séance du {format(parseISO(s.session_date), "d MMMM yyyy", { locale: fr })}
                        {s.source === "calendly" && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide bg-[#7B968B]/15 text-[#5e7a6e] rounded-full px-2 py-0.5 align-middle">
                            Calendly
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-[#757169] truncate max-w-md">
                        {s.transcript
                          ? `${s.transcript.slice(0, 90)}…`
                          : "Transcription à ajouter"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={`rounded-full font-medium ${meta.cls} border-0`}>
                      {meta.label}
                    </Badge>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => deleteSession(e, s.id)}
                      data-testid={`delete-session-${s.id}`}
                      title="Supprimer la séance"
                      className="p-2 rounded-lg text-[#C0BBB0] hover:text-[#B56B6B] hover:bg-[#B56B6B]/10 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <ClinicalSynthesis patientId={patientId} />

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-2xl bg-[#FBF9F6]">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#2D2C28]">Modifier la fiche</DialogTitle>
            <DialogDescription className="text-[#757169]">
              Mettez à jour les informations du patient.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[#575349]">Nom complet</Label>
              <Input
                data-testid="edit-name-input"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="mt-1.5 bg-white rounded-xl"
              />
            </div>
            <div>
              <Label className="text-[#575349]">Adresse e-mail</Label>
              <Input
                data-testid="edit-email-input"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="mt-1.5 bg-white rounded-xl"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#575349]">Téléphone</Label>
                <Input
                  data-testid="edit-phone-input"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="06 12 34 56 78"
                  className="mt-1.5 bg-white rounded-xl"
                />
              </div>
              <div>
                <Label className="text-[#575349]">Langue</Label>
                <Select value={editForm.language} onValueChange={(v) => setEditForm({ ...editForm, language: v })}>
                  <SelectTrigger data-testid="edit-language-select" className="mt-1.5 bg-white rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">🇫🇷 Français</SelectItem>
                    <SelectItem value="en">🇬🇧 English</SelectItem>
                    <SelectItem value="es">🇪🇸 Español</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[#575349]">Notes</Label>
              <Textarea
                data-testid="edit-notes-input"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                className="mt-1.5 bg-white rounded-xl min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={saveEdit}
              data-testid="save-edit-btn"
              className="rounded-xl bg-[#C07761] hover:bg-[#A66350] text-white w-full"
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

const NewSessionDialog = ({ open, setOpen, transcript, setTranscript, sessionDate, setSessionDate, creating, onCreate }) => (
  <Dialog open={open} onOpenChange={setOpen}>
    <DialogTrigger asChild>
      <Button
        data-testid="new-session-btn"
        className="rounded-xl bg-[#C07761] hover:bg-[#A66350] text-white h-11 px-5"
      >
        <FilePlus2 className="w-4 h-4 mr-2" /> Nouvelle séance
      </Button>
    </DialogTrigger>
    <DialogContent className="rounded-2xl bg-[#FBF9F6] max-w-2xl">
      <DialogHeader>
        <DialogTitle className="font-heading text-[#2D2C28]">Importer une séance</DialogTitle>
        <DialogDescription className="text-[#757169]">
          Collez la transcription de la séance issue de Plaud pour générer un e-mail de suivi.
        </DialogDescription>
      </DialogHeader>
      <div className="py-2 space-y-4">
        <div>
          <Label className="text-[#575349]">Date de la séance</Label>
          <Input
            data-testid="session-date-input"
            type="date"
            value={sessionDate}
            onChange={(e) => setSessionDate(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
            className="mt-1.5 bg-white rounded-xl sm:w-56"
          />
        </div>
        <div>
          <Label className="text-[#575349]">Transcription (depuis Plaud)</Label>
          <p className="text-xs text-[#9c988f] mt-1 mb-2">
            Copiez la transcription de la séance depuis votre application Plaud et collez-la ici.
          </p>
          <Textarea
            data-testid="transcript-input"
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            placeholder="Collez ici la transcription complète de la séance…"
            className="bg-white rounded-xl min-h-[220px] leading-relaxed"
          />
        </div>
      </div>
      <DialogFooter>
        <Button
          onClick={onCreate}
          disabled={creating}
          data-testid="create-session-btn"
          className="rounded-xl bg-[#C07761] hover:bg-[#A66350] text-white w-full"
        >
          <Mail className="w-4 h-4 mr-2" />
          {creating ? "Création…" : "Créer la séance"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default PatientDetail;
